import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { RequirePermission } from '@/components/RequirePermission';

// ---- Types ----------------------------------------------------------------

interface PermissionDto {
  id: string;
  key: string;
  description: string | null;
}

interface RoleDto {
  id: string;
  name: string;
  description: string | null;
  sortOrder: number;
  permissions: string[];
}

interface UserRoleEntry {
  roleId: string;
  roleName: string;
  isPrimary: boolean;
}

interface UserRolesDto {
  userId: string;
  roles: UserRoleEntry[];
}

// ---- Page -----------------------------------------------------------------

type Tab = 'roles' | 'catalogue';

export function PermissionsPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('roles');
  const [roles, setRoles] = useState<RoleDto[]>([]);
  const [catalogue, setCatalogue] = useState<PermissionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingRole, setEditingRole] = useState<RoleDto | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, catRes] = await Promise.all([
        apiClient.get<RoleDto[]>('/permissions/roles'),
        apiClient.get<PermissionDto[]>('/permissions/permissions'),
      ]);
      setRoles(rolesRes.data);
      setCatalogue(catRes.data);
    } catch {
      setRoles([]);
      setCatalogue([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  async function handleDeleteRole(id: string) {
    if (!window.confirm(t('permissions.roles.delete_confirm'))) return;
    try {
      await apiClient.delete(`/permissions/roles/${id}`);
      setToast(t('permissions.roles.deleted'));
      load();
    } catch {
      setToast(t('permissions.roles.delete_error'));
    }
  }

  return (
    <div className="space-y-4">
      {showCreate && (
        <CreateRoleModal
          catalogue={catalogue}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setToast(t('permissions.roles.created'));
            setShowCreate(false);
            load();
          }}
        />
      )}

      {editingRole && (
        <EditRoleModal
          role={editingRole}
          catalogue={catalogue}
          onClose={() => setEditingRole(null)}
          onSaved={() => {
            setToast(t('permissions.roles.updated'));
            setEditingRole(null);
            load();
          }}
        />
      )}

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('permissions.title')}</h1>
        <RequirePermission permission="tenant.admin.roles">
          <Button size="sm" onClick={() => setShowCreate(true)}>
            {t('permissions.roles.create')}
          </Button>
        </RequirePermission>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {(['roles', 'catalogue'] as Tab[]).map((t2) => (
          <button
            key={t2}
            onClick={() => setTab(t2)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              tab === t2
                ? 'border-b-2 border-primary text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t(`permissions.tabs.${t2}`)}
          </button>
        ))}
      </div>

      {loading && (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      )}

      {/* Roles tab */}
      {!loading && tab === 'roles' && (
        <div className="overflow-hidden rounded-lg border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-3">{t('permissions.roles.col_name')}</th>
                <th className="px-4 py-3">{t('permissions.roles.col_description')}</th>
                <th className="px-4 py-3">{t('permissions.roles.col_permissions')}</th>
                <th className="px-4 py-3">{t('permissions.roles.col_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr key={role.id} className="border-t">
                  <td className="px-4 py-3 font-medium">{role.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {role.description ?? '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {role.permissions.slice(0, 5).map((p) => (
                        <span
                          key={p}
                          className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs"
                        >
                          {p}
                        </span>
                      ))}
                      {role.permissions.length > 5 && (
                        <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                          +{role.permissions.length - 5}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <RequirePermission permission="tenant.admin.roles">
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingRole(role)}
                        >
                          {t('common.edit')}
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteRole(role.id)}
                        >
                          {t('common.delete')}
                        </Button>
                      </div>
                    </RequirePermission>
                  </td>
                </tr>
              ))}
              {roles.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-6 text-center text-muted-foreground"
                  >
                    {t('permissions.roles.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Catalogue tab */}
      {!loading && tab === 'catalogue' && (
        <div className="overflow-hidden rounded-lg border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-3">{t('permissions.catalogue.col_key')}</th>
                <th className="px-4 py-3">{t('permissions.catalogue.col_description')}</th>
              </tr>
            </thead>
            <tbody>
              {catalogue.map((p) => (
                <tr key={p.id} className="border-t">
                  <td className="px-4 py-3 font-mono text-xs font-medium">{p.key}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {p.description ?? '—'}
                  </td>
                </tr>
              ))}
              {catalogue.length === 0 && (
                <tr>
                  <td
                    colSpan={2}
                    className="px-4 py-6 text-center text-muted-foreground"
                  >
                    {t('permissions.catalogue.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-4 right-4 z-50 rounded-lg border bg-card px-4 py-3 text-sm shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}

// ---- Create Role Modal ----------------------------------------------------

function CreateRoleModal({
  catalogue,
  onClose,
  onCreated,
}: {
  catalogue: PermissionDto[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function togglePermission(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setError(t('permissions.roles.name_required'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await apiClient.post('/permissions/roles', {
        name: name.trim(),
        description: description.trim() || null,
        permissions: Array.from(selected),
      });
      onCreated();
    } catch {
      setError(t('permissions.roles.create_error'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg rounded-xl border bg-card p-6 shadow-xl">
        <h2 className="mb-4 text-lg font-semibold">
          {t('permissions.roles.create_title')}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium">
              {t('permissions.roles.name_label')}
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="CUSTOM_ROLE"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">
              {t('permissions.roles.description_label')}
            </label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('permissions.roles.description_placeholder')}
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium">
              {t('permissions.roles.permissions_label')}
            </label>
            <div className="max-h-48 overflow-y-auto rounded border bg-muted/30 p-2">
              <div className="grid grid-cols-2 gap-1">
                {catalogue.map((p) => (
                  <label key={p.key} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-muted">
                    <input
                      type="checkbox"
                      checked={selected.has(p.key)}
                      onChange={() => togglePermission(p.key)}
                      className="h-3.5 w-3.5"
                    />
                    <span className="font-mono text-xs">{p.key}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? t('common.save') + '…' : t('common.create')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---- Edit Role Modal ------------------------------------------------------

function EditRoleModal({
  role,
  catalogue,
  onClose,
  onSaved,
}: {
  role: RoleDto;
  catalogue: PermissionDto[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [description, setDescription] = useState(role.description ?? '');
  const [selected, setSelected] = useState<Set<string>>(
    new Set(role.permissions),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function togglePermission(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await apiClient.put(`/permissions/roles/${role.id}`, {
        description: description.trim() || null,
        permissions: Array.from(selected),
      });
      onSaved();
    } catch {
      setError(t('permissions.roles.save_error'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg rounded-xl border bg-card p-6 shadow-xl">
        <h2 className="mb-4 text-lg font-semibold">
          {t('permissions.roles.edit_title', { name: role.name })}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium">
              {t('permissions.roles.description_label')}
            </label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('permissions.roles.description_placeholder')}
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium">
              {t('permissions.roles.permissions_label')}
            </label>
            <div className="max-h-48 overflow-y-auto rounded border bg-muted/30 p-2">
              <div className="grid grid-cols-2 gap-1">
                {catalogue.map((p) => (
                  <label key={p.key} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-muted">
                    <input
                      type="checkbox"
                      checked={selected.has(p.key)}
                      onChange={() => togglePermission(p.key)}
                      className="h-3.5 w-3.5"
                    />
                    <span className="font-mono text-xs">{p.key}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? t('common.save') + '…' : t('common.save')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---- User Roles Modal (called from UsersPage) ------------------------------

export function UserRolesModal({
  userId,
  userEmail,
  onClose,
  onSaved,
}: {
  userId: string;
  userEmail: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [availableRoles, setAvailableRoles] = useState<RoleDto[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [primaryId, setPrimaryId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      apiClient.get<RoleDto[]>('/permissions/roles'),
      apiClient.get<UserRolesDto>(`/permissions/users/${userId}/roles`),
    ])
      .then(([rolesRes, userRolesRes]) => {
        setAvailableRoles(rolesRes.data);
        const ids = new Set(userRolesRes.data.roles.map((r) => r.roleId));
        setSelectedIds(ids);
        const primary = userRolesRes.data.roles.find((r) => r.isPrimary);
        setPrimaryId(primary?.roleId ?? null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  function toggleRole(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        if (primaryId === id) {
          const remaining = Array.from(next);
          setPrimaryId(remaining[0] ?? null);
        }
      } else {
        next.add(id);
        if (!primaryId) setPrimaryId(id);
      }
      return next;
    });
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await apiClient.put(`/permissions/users/${userId}/roles`, {
        roleIds: Array.from(selectedIds),
        primaryRoleId: primaryId,
        replace: true,
      });
      onSaved();
    } catch {
      setError(t('permissions.user_roles.save_error'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded-xl border bg-card p-6 shadow-xl">
        <h2 className="mb-1 text-lg font-semibold">
          {t('permissions.user_roles.title')}
        </h2>
        <p className="mb-4 text-sm text-muted-foreground">{userEmail}</p>

        {loading && (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        )}

        {!loading && (
          <div className="space-y-3">
            <p className="text-xs font-medium uppercase text-muted-foreground">
              {t('permissions.user_roles.assign_roles')}
            </p>
            <div className="space-y-1">
              {availableRoles.map((role) => {
                const checked = selectedIds.has(role.id);
                const isPrimary = primaryId === role.id;
                return (
                  <div
                    key={role.id}
                    className="flex items-center justify-between rounded border px-3 py-2"
                  >
                    <label className="flex cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleRole(role.id)}
                        className="h-3.5 w-3.5"
                      />
                      <span className="text-sm font-medium">{role.name}</span>
                      {isPrimary && (
                        <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-xs text-primary">
                          {t('permissions.user_roles.primary')}
                        </span>
                      )}
                    </label>
                    {checked && !isPrimary && (
                      <button
                        type="button"
                        onClick={() => setPrimaryId(role.id)}
                        className="text-xs text-muted-foreground hover:text-foreground"
                      >
                        {t('permissions.user_roles.make_primary')}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? t('common.save') + '…' : t('common.save')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
